








































































































































































import { Component, Prop, Vue } from "vue-property-decorator";
import FramedBox from "@/components/reports/partials/framed-box.vue";
import Checkbox from "@/components/core/controls/checkbox.vue";
import { CharacteristicLanguage, PdfLayout } from "@/models/graphql-global-types";
import { NewReportStore } from "@/store/NewReportStore";
import * as blobUtil from "blob-util";
import fileDownload from "js-file-download";
import LoadingButton from "@/components/core/controls/loading-button.component.vue";
import { sortLayouts } from "@/utils/layout-order";
import { ReportDetail_report } from "@/models/queries/types/ReportDetail";
import { PrintPdfSettings } from "@/models/queries/PrintPdfSettingsQuery";

enum PDFOption {
  OPEN,
  SAVE,
  SEND
}

@Component({
  components: {
    Checkbox,
    FramedBox,
    LoadingButton
  }
})
export default class PrintReportDialog extends Vue {
  @Prop() public fileName!: string;
  @Prop() public sheetIds!: string[];
  @Prop() public report!: ReportDetail_report | null;

  public selectedPdfLayout: PdfLayout | null = null;
  public selectedLanguage: CharacteristicLanguage | null = null;
  public didClickGenerate: boolean = false;
  public isSavingReport: boolean = false;
  public isSendingReport: boolean = false;
  public isOpeningReport: boolean = false;
  public settings?: PrintPdfSettings | null = null;
  public willSendEmail: boolean = false;
  public recipient: string = "";

  public emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  get sortedLayouts(): PdfLayout[] {
    return sortLayouts(this.report?.availablePdfLayouts ?? []);
  }

  get chunkedLanguages(): Array<string[]> {
    return [[CharacteristicLanguage.en, CharacteristicLanguage.de, CharacteristicLanguage.zh]];
  }

  public isLangDisabled(lang: string): boolean {
    return (
      lang === "de" && (this.selectedPdfLayout === PdfLayout.DECKMATE || this.selectedPdfLayout === PdfLayout.DEMO)
    );
  }

  public didSelectLayout(layout: PdfLayout) {
    this.selectedPdfLayout = layout;
    if ((layout === PdfLayout.DEMO || PdfLayout.DECKMATE) && this.selectedLanguage === CharacteristicLanguage.en) {
      this.selectedLanguage = null;
    }
  }

  public didSelectLanguage(lang: CharacteristicLanguage) {
    this.selectedLanguage = lang;
  }

  public toggleSettings(setting: "pdf" | "image" | "characteristic" | "material_certificate" | "technical_drawing") {
    if (!this.settings) {
      return;
    }
    switch (setting) {
      case "pdf":
        this.settings.pdfAttachments = !this.settings.pdfAttachments;
        break;
      case "image":
        this.settings.imageAttachments = !this.settings.imageAttachments;
        break;
      case "characteristic":
        this.settings.characteristicAttachments = !this.settings.characteristicAttachments;
        break;
      case "material_certificate":
        this.settings.materialCertificate = !this.settings.materialCertificate;
        break;
      case "technical_drawing":
        this.settings.technicalDrawing = !this.settings.technicalDrawing;
        break;
    }
  }

  public savePDF() {
    this.generatePdf(PDFOption.SAVE);
  }

  public openPDF() {
    this.generatePdf(PDFOption.OPEN);
  }

  public generatePdf(option: PDFOption) {
    this.didClickGenerate = true;
    if (this.selectedPdfLayout && this.selectedLanguage && !this.isSavingReport && this.settings) {
      this.isSavingReport = option === PDFOption.SAVE;
      this.isOpeningReport = option === PDFOption.OPEN;
      NewReportStore.printPdfReport({
        reportSecret: this.report?.secret ?? "",
        language: this.selectedLanguage,
        layout: this.selectedPdfLayout,
        sheetIds: this.sheetIds,
        settings: {
          pdfAttachments: this.settings.pdfAttachments,
          imageAttachments: this.settings.imageAttachments,
          characteristicAttachments: this.settings.characteristicAttachments,
          technicalDrawing: this.settings.technicalDrawing,
          materialCertificate: this.settings.materialCertificate
        }
      })
        .then(base64 => {
          this.isSavingReport = false;
          this.isOpeningReport = false;
          switch (option) {
            case PDFOption.OPEN:
              const url = URL.createObjectURL(blobUtil.base64StringToBlob(base64));
              let pdfWindow = window.open(this.fileName);
              pdfWindow?.document.write(
                `<iframe width='100%' height='100%' title='${this.fileName}' src='data:application/pdf;base64,
                  ${base64}
                  ' style="  position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    right: 0;
                    width: 100%;
                    height: 100%;
                    border: none;
                    margin: 0;
                    padding: 0;
                    overflow: hidden;
                    z-index: 999999;"></iframe>`
              );
              break;
            case PDFOption.SAVE:
              fileDownload(blobUtil.base64StringToBlob(base64), this.fileName);
              break;
          }

          this.close();
        })
        .catch(e => {
          this.isSavingReport = false;
          this.isOpeningReport = false;
          this.$notify({
            title: this.$t("general.error.title") as string,
            text: this.$t("general.error.generic_error") as string,
            type: "error"
          });
        });
    }
  }

  public async sendPDF() {
    if (!this.willSendEmail) {
      this.willSendEmail = true;
      return;
    }

    if (!this.emailRegex.test(this.recipient)) {
      return;
    }

    this.didClickGenerate = true;
    if (this.selectedPdfLayout && this.selectedLanguage && !this.isSendingReport && this.settings) {
      this.isSendingReport = true;
      NewReportStore.sendPdfReport({
        reportSecret: this.report?.secret ?? "",
        language: this.selectedLanguage,
        layout: this.selectedPdfLayout,
        sheetIds: this.sheetIds,
        recipientEmail: this.recipient,
        settings: {
          pdfAttachments: this.settings.pdfAttachments,
          imageAttachments: this.settings.imageAttachments,
          characteristicAttachments: this.settings.characteristicAttachments,
          technicalDrawing: this.settings.technicalDrawing,
          materialCertificate: this.settings.materialCertificate
        }
      })
        .then(result => {
          this.isSendingReport = false;
          if (!result) {
            throw new Error("didn't work");
          }
          this.close();
        })
        .catch(e => {
          this.isSendingReport = false;
          this.$notify({
            title: this.$t("general.error.title") as string,
            text: this.$t("general.error.generic_error") as string,
            type: "error"
          });
        });
    }
  }

  public close() {
    // @ts-ignore
    $("#printReportDialog").modal("hide");
  }

  public mounted() {
    NewReportStore.getPrintPdfSettings().then(settings => {
      this.settings = Object.assign({}, settings);
    });

    $("#printReportDialog").on("show.bs.modal", () => {
      const settings = NewReportStore.printPdfSettings;
      this.selectedLanguage = CharacteristicLanguage.en;
      this.selectedPdfLayout =
        settings?.defaultLayout && this.sortedLayouts.includes(settings?.defaultLayout)
          ? settings?.defaultLayout
          : PdfLayout.STANDARD;
      this.didClickGenerate = false;
      this.settings = Object.assign({}, settings);
      this.willSendEmail = false;
      this.recipient = "";
    });
  }
}
